import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, Type } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Composicion {
  id: number;
  link_img: string;
  categoria: string;
  nombre?: string;
}

export const SeleccionarTextoEstudioVirtual: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0'),
    [idsParam],
  );

  const textoCompIdParam = params.get('texto_comp_id') || '';
  const textoCompUrlParam = params.get('texto_comp_url') || '';
  const textoTextParam = params.get('texto_texto') || '';

  const [composiciones, setComposiciones] = useState<Composicion[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [selectedCompUrl, setSelectedCompUrl] = useState<string>(textoCompUrlParam);
  const [selectedCompId, setSelectedCompId] = useState<number | null>(() => {
    if (!textoCompIdParam) return null;
    const n = Number(textoCompIdParam);
    return Number.isFinite(n) ? n : null;
  });
  const [compositionText, setCompositionText] = useState<string>(textoTextParam);

  useEffect(() => {
    const fetchComposiciones = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const { data, error } = await supabase
          .schema('public')
          .from('composiciones')
          .select('id, link_img, categoria, nombre');
        if (error) throw error;
        setComposiciones((data as any) || []);
      } catch (e) {
        console.error('Error cargando composiciones', e);
        setFetchError(e instanceof Error ? e.message : 'Error al cargar composiciones');
      } finally {
        setLoading(false);
      }
    };
    fetchComposiciones();
  }, []);

  const compsPorCategoria = useMemo(() => {
    const map = new Map<string, Composicion[]>();
    for (const c of composiciones) {
      const key = c.categoria || 'Otros';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    }
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === 'Moderno' && b[0] !== 'Moderno') return -1;
      if (b[0] === 'Moderno' && a[0] !== 'Moderno') return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [composiciones]);

  const [activeCategoria, setActiveCategoria] = useState<string>('');
  const categorias = useMemo(() => compsPorCategoria.map(([c]) => c), [compsPorCategoria]);

  useEffect(() => {
    if (!activeCategoria && categorias.length > 0) {
      setActiveCategoria(categorias[0]);
    }
  }, [categorias, activeCategoria]);

  const itemsCategoriaActual = useMemo(() => {
    const entry = compsPorCategoria.find(([c]) => c === activeCategoria);
    return entry ? entry[1] : [];
  }, [compsPorCategoria, activeCategoria]);

  const pickComposicion = (url: string, id?: number) => {
    setSelectedCompUrl(url);
    if (typeof id === 'number') setSelectedCompId(id);
    else setSelectedCompId(null);
  };

  const handleDone = () => {
    const qp = new URLSearchParams(location.search);
    const trimmed = compositionText.trim();
    const validId = typeof selectedCompId === 'number' && Number.isFinite(selectedCompId);
    if (!validId || !trimmed) {
      // Defensa extra: no guardar nada si no hay plantilla seleccionada con texto.
      return;
    }

    qp.set('texto_enabled', '1');
    qp.set('texto_comp_id', String(selectedCompId));
    if (selectedCompUrl) {
      qp.set('texto_comp_url', selectedCompUrl);
    } else {
      qp.delete('texto_comp_url');
    }
    qp.set('texto_texto', trimmed);

    navigate(`/redes-sociales/crear-anuncio/prueba-nueva-interfaz?${qp.toString()}`);
  };

  const canSave =
    typeof selectedCompId === 'number' &&
    Number.isFinite(selectedCompId) &&
    compositionText.trim().length > 0;

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      {/* Mobile sticky header */}
      <>
        <div 
          className="sm:hidden fixed top-0 left-0 right-0 h-20 z-30 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0) 100%)' }}
        />
        <div className="sm:hidden fixed top-4 left-4 right-4 z-40 flex items-center justify-between h-10">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm"
            aria-label="Atrás"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-900">Añadir texto</h1>
          <button
            type="button"
            onClick={handleDone}
            disabled={!canSave}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full shadow-sm ${
              canSave ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            aria-label="Listo"
          >
            <Check className="w-5 h-5" />
          </button>
        </div>
      </>

      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6 mt-14 sm:mt-0">

        {/* Desktop header */}
        <div className="hidden sm:flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm"
            aria-label="Atrás"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Añadir texto</h1>
          <button
            type="button"
            onClick={handleDone}
            disabled={!canSave}
            className={`inline-flex items-center justify-center w-11 h-11 rounded-full shadow-sm ${
              canSave ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            aria-label="Listo"
          >
            <Check className="w-5 h-5" />
          </button>
        </div>

        {fetchError && (
          <div className="mb-3 bg-red-50 text-red-700 px-4 py-3 text-sm">
            {fetchError}
          </div>
        )}

        {/* Biblioteca de composiciones */}
        <div className="mt-6">
            {loading && <span className="text-sm text-gray-500">Cargando…</span>}
            {!loading && compsPorCategoria.length === 0 && (
              <p className="text-sm text-gray-600 mt-2">No hay composiciones disponibles por el momento.</p>
            )}
            {!loading && compsPorCategoria.length > 0 && (
              <>
                {/* Categorías horizontales */}
                <div className="mt-3 overflow-x-auto">
                  <div className="inline-flex gap-2">
                    {categorias.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCategoria(cat)}
                        className={`px-4 h-9 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap ${
                          activeCategoria === cat
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grilla para la categoría activa */}
                <div className="mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    {itemsCategoriaActual.map((comp) => {
                      const isSelected = selectedCompId === comp.id;
                      return (
                        <div
                          key={comp.id}
                          className={`w-full text-left border rounded-2xl overflow-hidden py-3 px-2 transition-colors ${
                            isSelected
                              ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500 sm:col-span-2'
                              : 'border-gray-200'
                          }`}
                        >
                          <div className={`flex ${isSelected ? 'flex-col sm:flex-row' : 'flex-col'} gap-3`}>
                            <button
                              type="button"
                              onClick={() => pickComposicion(comp.link_img, comp.id)}
                              className={`relative overflow-hidden rounded-xl h-28 ${
                                isSelected ? 'sm:w-48' : 'w-full'
                              } flex-shrink-0 focus:outline-none hover:bg-gray-50`}
                            >
                              <img
                                src={comp.link_img}
                                alt={comp.nombre || `Composición ${comp.id}`}
                                className="w-full h-full object-cover"
                              />
                              {isSelected && (
                                <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                                  <Check className="w-4 h-4" />
                                </span>
                              )}
                            </button>
                            {isSelected && (
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700">Texto</label>
                                <textarea
                                  value={compositionText}
                                  onChange={(e) => setCompositionText(e.target.value)}
                                  placeholder="Escribí tu texto..."
                                  rows={4}
                                  className="mt-1 block w-full rounded-md border-0 resize-none bg-transparent text-sm focus:outline-none"
                                />
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 mt-2 truncate">{comp.nombre || 'Composición'}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
};

export default SeleccionarTextoEstudioVirtual;
